<template>
  <div class="rbz-columns">
    <div class="rbz-column is-5" :class="selectedTypeColumnClass">
      <rbz-dropdown
        :items="types"
        :value="localOptionType"
        @update:value="typeChanged"
        class="option-type-selection"
        data-qa="option-type-dropdown"
        :disabled="disabled"
      />
    </div>
    <div class="rbz-column is-7" :class="selectedNameColumnClass" v-if="localOptionType !== 'self'">
      <rbz-form-input
        v-if="isCategorySimpleInput(localOptionType)"
        name="option-name-input"
        v-model:inputValue="localOption[localOptionType]"
        @update:inputValue="emitOptionUpdate"
        class="option-name-input"
        data-qa="option-name-input"
        :disabled="disabled"
      />
      <rbz-dropdown
        v-if="localOptionType === 'attrs'"
        :items="Object.entries(attributes)"
        v-model:value="localOption[localOptionType]"
        @update:value="emitOptionUpdate"
        class="option-attribute-selection"
        data-qa="option-attribute-dropdown"
        :disabled="disabled"
      />
    </div>
    <div class="rbz-column is-narrow align-items-center" v-if="!!showRemove">
      <rbz-form-button
        :disabled="!removable || disabled"
        class="remove-option-button"
        data-qa="remove-option-button"
        variant="tertiary"
        icon="IconDelete"
        v-b-tooltip="disabled ? '' : $t('keyOption.removeEntryTooltip')"
        @button-click="emitOptionRemove"
      />
    </div>
  </div>
</template>

<script lang="ts">
import _ from 'lodash';
import { defineComponent, PropType } from 'vue';
import { KeyOptionType } from '@/types';
import RbzFormButton from '@/components/RbzFormButton.vue';
import RbzDropdown from '@/components/RbzDropdown.vue';
import RbzFormInput from '@/components/RbzFormInput.vue';

type KeyRuleType = 'headers' | 'args' | 'cookies' | 'attrs' | 'plugins.jwt' | 'self';

export default defineComponent({
  name: 'KeyOption',
  components: {
    RbzFormButton,
    RbzDropdown,
    RbzFormInput,
  },
  props: {
    option: {
      type: Object as PropType<KeyOptionType>,
      default: (): KeyOptionType => {
        return {
          attrs: '',
        } as KeyOptionType;
      },
    },
    showRemove: {
      type: Boolean,
      default: false,
    },
    removable: {
      type: Boolean,
      default: false,
    },
    useDefaultSelf: {
      type: Boolean,
      default: false,
    },
    ignoreAttributes: {
      type: Array,
      default: () => {
        return [] as string[];
      },
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    selectedTypeColumnClass: String,
    selectedNameColumnClass: String,
  },
  data() {
    return {
      keyAttributes: {
        asn: this.$t('keyOption.asn'),
        authority: this.$t('keyOption.authority'),
        country: this.$t('keyOption.country'),
        ip: this.$t('keyOption.ip'),
        method: this.$t('keyOption.method'),
        network: this.$t('keyOption.network'),
        organization: this.$t('keyOption.organization'),
        path: this.$t('keyOption.path'),
        securitypolicyentryname: this.$t('keyOption.securityPolicyEntryName'),
        query: this.$t('keyOption.query'),
        region: this.$t('keyOption.region'),
        securitypolicyname: this.$t('keyOption.securityPolicyName'),
        subregion: this.$t('keyOption.subregion'),
        tags: this.$t('keyOption.tag'),
        uri: this.$t('keyOption.uri'),
        session: this.$t('keyOption.session'),
      },
      options: {
        headers: this.$t('keyOption.header'),
        cookies: this.$t('keyOption.cookie'),
        args: this.$t('keyOption.argument'),
        attrs: this.$t('keyOption.attribute'),
        'plugins.jwt': this.$t('keyOption.plugin.jwt'),
      },
    };
  },
  computed: {
    localOption(): KeyOptionType {
      const selectedOptionKey = Object.keys(this.option)[0];
      if (selectedOptionKey?.startsWith('plugins')) {
        const selectedOptionValue = Object.values(this.option)[0];
        const pluginType = selectedOptionValue.split('.')[0];
        const pluginValue = selectedOptionValue.split('.')[1];
        const returnValue: KeyOptionType = {};
        returnValue[`plugins.${pluginType}`] = pluginValue;
        return returnValue;
      } else {
        const clonedOption = _.cloneDeep(this.option as KeyOptionType);
        if (clonedOption.attrs && (clonedOption.attrs === 'tag' || clonedOption.attrs === 'Tag')) {
          clonedOption.attrs = 'tags';
        }
        return clonedOption;
      }
    },

    types() {
      const types = Object.entries(this.options);
      if (this.useDefaultSelf) {
        types.unshift(['self', this.$t('keyOption.httpRequest')]);
      }
      return types;
    },

    localOptionType(): KeyRuleType {
      return Object.keys(this.localOption)[0] as KeyRuleType;
    },

    attributes() {
      return _.pickBy(this.keyAttributes, (value, key) => {
        return !this.ignoreAttributes || !this.ignoreAttributes.includes(key);
      });
    },
  },
  emits: ['update:option', 'remove'],
  methods: {
    emitOptionUpdate() {
      const selectedOptionKey = Object.keys(this.localOption)[0];
      if (selectedOptionKey?.startsWith('plugins')) {
        const selectedOptionValue = Object.values(this.localOption)[0];
        const pluginType = selectedOptionKey.split('.')[1];
        this.$emit('update:option', { plugins: `${pluginType}.${selectedOptionValue}` });
      } else {
        this.$emit('update:option', this.localOption);
      }
    },

    emitOptionRemove() {
      this.$emit('remove');
    },

    isCategorySimpleInput(type: KeyRuleType) {
      return new RegExp('(args|cookies|headers|plugins)').test(type);
    },

    typeChanged(newType: KeyRuleType) {
      delete this.localOption[this.localOptionType];
      if (this.isCategorySimpleInput(newType)) {
        this.localOption[newType] = '';
      }
      if (newType === 'attrs') {
        this.localOption[newType] = 'ip';
      }
      if (newType === 'self') {
        this.localOption[newType] = 'self';
      }
      this.emitOptionUpdate();
    },
  },
});
</script>
